import { ChangeEvent, Dispatch, SetStateAction, useCallback } from 'react';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { MspCategoryDigitCodesEnum } from 'APIPackage/KnmModule/enums/MspCategoryDigitCodesEnum';
import { mspCodesByMspCategoryDigitCodes } from 'KnmPackage/MainModule/constants/mspCodesByMspCategoryDigitCodes';
import { checkOgrnBySubjectType } from 'CorePackage/MainModule/validators/isOgrn';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { findRegionSelectOption } from 'KnmPackage/MainModule/components/regions';

import { IPmErknmSubjectFieldPaths } from './usePmErknmSubjectFields';
import { clearPmSubjectFields } from '../common/clearPmSubjectFields';

// eslint-disable-next-line @typescript-eslint/naming-convention
interface UsePmErknmSubjectHandlersProps {
  formRef: IKnmFormRef | undefined;
  isChangeSubjectTypeFromActionsAvailable: boolean;
  isNew: boolean;
  subjectFieldNames: IPmErknmSubjectFieldPaths;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  isUR: boolean;
  isIP: boolean;
  name: string;
  setCheckByInnMessage?: Dispatch<SetStateAction<string | undefined>>;
  rafpNza: string | undefined;
  setRafpNzaNotFoundText: Dispatch<SetStateAction<string | undefined>>;
  isRafpNzaNotFound: boolean;
  rafpNotFoundMessage: string;
  districtSelectOptions: IKnmDistrictSelectOption[];
}

export const usePmErknmSubjectHandlers = ({
  formRef,
  isChangeSubjectTypeFromActionsAvailable,
  isNew,
  subjectFieldNames,
  subjectType,
  isUR,
  isIP,
  name,
  setCheckByInnMessage,
  rafpNza,
  setRafpNzaNotFoundText,
  isRafpNzaNotFound,
  rafpNotFoundMessage,
  districtSelectOptions,
}: UsePmErknmSubjectHandlersProps) => {
  const changeFormField = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (field: string, value1: any) => formRef?.current?.change(field as keyof IKnmForm, value1 as IKnmForm),
    [formRef],
  );
  const remove = formRef?.current.mutators.remove;

  const clearObjectsNotFromErvk = useCallback(() => {
    if (!formRef || !remove) return;
    const objectsPmFieldName: keyof IKnmForm = 'objectsPm';
    const currentValues = formRef.current?.getState().values;
    const objectsArray = currentValues[objectsPmFieldName] || [];
    const indicesToRemove: number[] = objectsArray.flatMap((obj, index) => (!obj.ervkId ? [index] : []));

    // Удаляем с конца: remove сдвигает индексы оставшихся элементов массива.
    indicesToRemove.reverse().forEach(index => {
      remove(objectsPmFieldName, index);
    });
  }, [formRef, remove]);

  const resetFields = useCallback(() => {
    clearPmSubjectFields({ formRef, subjectFieldNames });

    if (!isNew && isChangeSubjectTypeFromActionsAvailable) clearObjectsNotFromErvk();
  }, [formRef, subjectFieldNames, isNew, isChangeSubjectTypeFromActionsAvailable, clearObjectsNotFromErvk]);

  const handleLegalEntityAutoCompleteSelect = useCallback(
    async (legalEntityLookup: ILegalEntityLookup) => {
      const change = changeFormField;

      const {
        ogrn: legalEntityOgrn,
        inn: legalEntityInn,
        orgName: legalEntityOrgName,
        leAddress: legalEntityLeAddress,
        workAddress: legalEntityWorkAddress,
        mspCategory: legalMspCategory,
        mspRegistryDate: legalMspRegistryDate,
        leAddressRegionCode,
        leAddressIsFromFias,
      } = legalEntityLookup;

      change(subjectFieldNames.organizationAutocomplete, true);
      if (legalEntityOgrn) {
        change(subjectFieldNames.ogrn, legalEntityOgrn);
      }
      if (legalEntityInn) {
        change(subjectFieldNames.inn, legalEntityInn);
      }
      if (legalEntityOrgName && checkOgrnBySubjectType(subjectType?.label, legalEntityOgrn)) {
        change(subjectFieldNames.organizationName, legalEntityOrgName);
      }
      if (legalEntityLeAddress && isUR) {
        change(subjectFieldNames.locationAddress, legalEntityLeAddress);
        const isInFias = leAddressIsFromFias === 'true';

        change(subjectFieldNames.locationnotInFIAS, !isInFias);
        change(subjectFieldNames.locationRegionCodeFromFns, leAddressRegionCode);

        const regionOption = await findRegionSelectOption(
          legalEntityLeAddress,
          leAddressRegionCode || undefined,
          districtSelectOptions,
        );

        const finalLocationRegionField = isInFias
          ? subjectFieldNames.locationfiasRegion
          : subjectFieldNames.locationregion;

        change(finalLocationRegionField, regionOption);
      }
      if (legalEntityLeAddress && isIP) {
        change(subjectFieldNames.factualAddress, legalEntityLeAddress);
      }
      if (legalEntityWorkAddress) {
        change(subjectFieldNames.workAddress, legalEntityWorkAddress);
      }
      if (legalMspCategory) {
        change(
          subjectFieldNames.mspCode,
          mspCodesByMspCategoryDigitCodes[Number(legalMspCategory) as MspCategoryDigitCodesEnum],
        );
      }
      if (legalMspRegistryDate) {
        change(subjectFieldNames.mspRegistryDate, legalMspRegistryDate);
      }
    },
    [formRef, isUR, isIP],
  );

  const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();

  const handleAutoCompleteClear = useCallback(() => {
    const change = (field: string) => changeFormField(field, undefined);

    change(subjectFieldNames.smevRequestStatus);
    change(subjectFieldNames.ogrn);
    change(subjectFieldNames.inn);
    change(subjectFieldNames.organizationName);
    change(subjectFieldNames.locationAddress);
    change(subjectFieldNames.workAddress);
    change(subjectFieldNames.factualAddress);
    change(subjectFieldNames.mspCode);
    dispatchKnmSetLegalEntityData(undefined);
    change(subjectFieldNames.organizationAutocomplete);
    const objects = formRef?.current?.getFieldState(`${name}objectsPm` as keyof Pick<IKnmForm, 'objectsPm'>)?.value;
    const autocompleteObjectIndex = objects?.findIndex(object => object.autocomplete);

    if (typeof autocompleteObjectIndex === 'number' && autocompleteObjectIndex >= 0) {
      const autocompleteFieldName = `${name}objectsPm[${autocompleteObjectIndex}].autocomplete` as keyof IKnmForm;

      formRef?.current?.change(autocompleteFieldName, false);
    }
  }, [dispatchKnmSetLegalEntityData, formRef]);

  const onClear = useCallback(() => {
    const change = (field: string) => changeFormField(field, undefined);

    change(subjectFieldNames.subjectTypePm);
    resetFields();
    handleAutoCompleteClear();
  }, [changeFormField, subjectFieldNames, resetFields, handleAutoCompleteClear]);

  const subjectTypeChange = useCallback(
    (value: IDictionarySelectOption<ITargetEntity> | undefined) => {
      const change = changeFormField;

      resetFields();
      handleAutoCompleteClear();
      change(subjectFieldNames.isFiz, value?.label === KnmSubjectTypesEnum.FIZ);
      setCheckByInnMessage && setCheckByInnMessage(undefined);
    },
    [resetFields, formRef],
  );

  const handleRafpNzaChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      const change = (field: string) => changeFormField(field, undefined);
      // Сбросить ИНН и КПП

      if (v.target.value) {
        change(subjectFieldNames.inn);
        change(subjectFieldNames.kpp);
        if (v.target.value !== rafpNza) {
          setRafpNzaNotFoundText(undefined);
        } else if (isRafpNzaNotFound) {
          setRafpNzaNotFoundText(rafpNotFoundMessage);
        }
      }
    },
    [formRef, subjectFieldNames],
  );

  return {
    handleLegalEntityAutoCompleteSelect,
    handleAutoCompleteClear,
    onClear,
    subjectTypeChange,
    handleRafpNzaChange,
  };
};
